import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Candidate, CandidateListItem, CreatorClassificationEnum } from "@unbox/api";
import { CANDIDATES_NEGATABLE_FILTERS } from "@unbox/api";
import { useCallback } from "react";
import { handleError } from "@/api/error";
import type { ChunkResponse } from "@/hooks/use-progressive-fetch";
import { toNotQuery } from "@/providers/filter-registry";
import type { Pagination } from "./pagination";
import { client, getScopedMerchantClient, useScopedMerchantClient } from "./scoped-merchant-client";

type CandidateSort = {
  column:
    | "nickname"
    | "followerCount"
    | "gmvAmount"
    | "unitsSold"
    | "gpmAmount"
    | "ecVideoCount"
    | "avgEcVideoPlayCount"
    | "sellerRating"
    | "matchQualityScore";
  direction: "asc" | "desc";
};

export type CandidateClassification = CreatorClassificationEnum;

type AudiencePercentThreshold = ">25" | ">50" | ">75";

export type CandidateAttributeFilters = {
  text?: string;
  minFollowers?: number;
  maxFollowers?: number;
  minGmv?: number;
  maxGmv?: number;
  minLiveGmv?: number;
  maxLiveGmv?: number;
  minVideoGmv?: number;
  maxVideoGmv?: number;
  minUnitsSold?: number;
  maxUnitsSold?: number;
  minRating?: number;
  maxRating?: number;
  maleAudiencePercent?: AudiencePercentThreshold;
  femaleAudiencePercent?: AudiencePercentThreshold;
  toneAndApproach?: string[];
  contentFocus?: string[];
  productionQuality?: string[];
  topSellingCategories?: string[];
  minTotalLikes?: number;
  maxTotalLikes?: number;
  minPostingFrequency?: number;
  maxPostingFrequency?: number;
  creatorLevel?: string[];
  categoryPro?: string[];
  creatorLanguage?: string[];
  not?: string[];
};

type CandidateFilters = CandidateAttributeFilters & {
  gender?: "male" | "female";
  age?: ("18-24" | "25-34" | "35-44" | "45-54" | "55+")[];
  classification?: CandidateClassification;
  collaborationStatus?: "invited" | "not_invited";
  candidateList?: string;
  includeCohortIds?: string[];
  excludeCohortIds?: string[];
};

export const buildCandidateAttributeQuery = (filters: CandidateAttributeFilters) => ({
  text: filters.text === "" ? undefined : filters.text,
  minFollowers: filters.minFollowers?.toString(),
  maxFollowers: filters.maxFollowers?.toString(),
  minGmv: filters.minGmv?.toString(),
  maxGmv: filters.maxGmv?.toString(),
  minLiveGmv: filters.minLiveGmv?.toString(),
  maxLiveGmv: filters.maxLiveGmv?.toString(),
  minVideoGmv: filters.minVideoGmv?.toString(),
  maxVideoGmv: filters.maxVideoGmv?.toString(),
  minUnitsSold: filters.minUnitsSold?.toString(),
  maxUnitsSold: filters.maxUnitsSold?.toString(),
  minRating: filters.minRating?.toString(),
  maxRating: filters.maxRating?.toString(),
  maleAudiencePercent: filters.maleAudiencePercent,
  femaleAudiencePercent: filters.femaleAudiencePercent,
  toneAndApproach: filters.toneAndApproach,
  contentFocus: filters.contentFocus,
  productionQuality: filters.productionQuality,
  topSellingCategories: filters.topSellingCategories,
  minTotalLikes: filters.minTotalLikes?.toString(),
  maxTotalLikes: filters.maxTotalLikes?.toString(),
  minPostingFrequency: filters.minPostingFrequency?.toString(),
  maxPostingFrequency: filters.maxPostingFrequency?.toString(),
  creatorLevel: filters.creatorLevel,
  categoryPro: filters.categoryPro,
  creatorLanguage: filters.creatorLanguage,
});

const buildCandidatesApiQuery = (filters: CandidateFilters) => ({
  ...buildCandidateAttributeQuery(filters),
  gender: filters.gender,
  age: filters.age,
  classification: filters.classification,
  collaborationStatus: filters.collaborationStatus,
  candidateList: filters.candidateList,
  includeCohortIds: filters.includeCohortIds,
  excludeCohortIds: filters.excludeCohortIds,
  not: toNotQuery(filters.not, CANDIDATES_NEGATABLE_FILTERS),
});

// IMPORTANT: The query key is shared between `useCandidates` and
// `useCandidateRowsFetcher` so that `queryClient.fetchQuery` deduplicates
// against already-mounted queries. Update whenever the filter shape changes.
const candidatesFilterKeyParts = (filters: CandidateFilters) =>
  [
    filters.minRating,
    filters.maxRating,
    filters.minFollowers,
    filters.maxFollowers,
    filters.minGmv,
    filters.maxGmv,
    filters.minLiveGmv,
    filters.maxLiveGmv,
    filters.minVideoGmv,
    filters.maxVideoGmv,
    filters.minUnitsSold,
    filters.maxUnitsSold,
    filters.text,
    filters.gender,
    filters.maleAudiencePercent,
    filters.femaleAudiencePercent,
    filters.age,
    filters.classification,
    filters.toneAndApproach?.toString(),
    filters.contentFocus?.toString(),
    filters.productionQuality?.toString(),
    filters.topSellingCategories?.toString(),
    filters.minTotalLikes,
    filters.maxTotalLikes,
    filters.minPostingFrequency,
    filters.maxPostingFrequency,
    filters.collaborationStatus,
    filters.candidateList,
    filters.creatorLevel?.toString(),
    filters.categoryPro?.toString(),
    filters.creatorLanguage?.toString(),
    filters.includeCohortIds?.toString(),
    filters.excludeCohortIds?.toString(),
    filters.not?.toString(),
  ] as const;

const candidatesListKey = (
  merchant: string,
  workspaceId: string | null,
  filters: CandidateFilters,
  pagination: Pagination,
  sort: CandidateSort,
) =>
  [
    "candidates",
    merchant,
    workspaceId,
    pagination.limit,
    pagination.offset,
    sort.column,
    sort.direction,
    ...candidatesFilterKeyParts(filters),
  ] as const;

// Returns { candidates, totalCount } — the single source of truth for both
// the row data and the total count (Phase 3: no separate /count request).
const fetchCandidatesList = async (
  merchant: string,
  workspaceId: string | null,
  filters: CandidateFilters,
  pagination: { limit: number; offset: number },
  sort?: CandidateSort,
) => {
  const scope = getScopedMerchantClient(merchant, workspaceId);
  const res = scope.workspaceId
    ? await client.workspace[":workspaceId"].merchant[":merchantId"].candidates.$get({
        param: scope.param,
        query: {
          ...buildCandidatesApiQuery(filters),
          limit: pagination.limit.toString(),
          offset: pagination.offset.toString(),
          ...(sort ? { column: sort.column, direction: sort.direction } : {}),
        },
      })
    : await client.merchant[":merchantId"].candidates.$get({
        param: scope.param,
        query: {
          ...buildCandidatesApiQuery(filters),
          limit: pagination.limit.toString(),
          offset: pagination.offset.toString(),
          ...(sort ? { column: sort.column, direction: sort.direction } : {}),
        },
      });
  if (!res.ok) throw await handleError(res);
  return await res.json();
};

export const useCandidates = ({
  pagination,
  sort,
  filters,
}: {
  pagination: Pagination;
  sort: CandidateSort;
  filters: CandidateFilters;
}) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: candidatesListKey(scope.merchantId, scope.workspaceId, filters, pagination, sort),
    queryFn: () => fetchCandidatesList(scope.merchantId, scope.workspaceId, filters, pagination, sort),
    enabled: !!scope.merchantId,
  });
};

export const useCandidateRowsFetcher = (filters: CandidateFilters) => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useCallback(
    async (offset: number, limit: number): Promise<ChunkResponse<CandidateListItem>> => {
      const pagination = { limit, offset };
      const fetcherSort: CandidateSort = { column: "gmvAmount", direction: "desc" };

      const listData = await queryClient.fetchQuery({
        queryKey: [...candidatesListKey(scope.merchantId, scope.workspaceId, filters, pagination, fetcherSort)],
        queryFn: () => fetchCandidatesList(scope.merchantId, scope.workspaceId, filters, pagination, fetcherSort),
        staleTime: Number.POSITIVE_INFINITY,
      });

      return { items: listData.candidates, total: listData.totalCount };
    },
    [queryClient, scope.merchantId, scope.workspaceId, filters],
  );
};

export const useCandidateFilters = () => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["candidate-filters", scope.merchantId, scope.workspaceId],
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].candidates.filters.$get({
            param: scope.param,
          })
        : await client.merchant[":merchantId"].candidates.filters.$get({
            param: scope.param,
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useCandidate = (candidateId?: string) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["candidate", scope.merchantId, scope.workspaceId, candidateId],
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].candidates[":candidateId"].$get({
            param: { ...scope.param, candidateId: candidateId ?? "" },
          })
        : await client.merchant[":merchantId"].candidates[":candidateId"].$get({
            param: { ...scope.param, candidateId: candidateId ?? "" },
          });
      if (!res.ok) {
        if (res.status === 404) {
          return null;
        }
        throw await handleError(res);
      }
      return await res.json();
    },
    enabled: !!scope.merchantId && !!candidateId,
  });
};

const CHUNK_SIZE = 200;

export const useCandidatesDownload = () => {
  const scope = useScopedMerchantClient();

  return useMutation<
    CandidateListItem[],
    Error,
    {
      total: number;
      sort: CandidateSort;
      filters: CandidateFilters;
    }
  >({
    mutationFn: async ({ total, sort, filters }) => {
      const chunks = [...Array(Math.ceil(total / CHUNK_SIZE)).keys()];

      const requests = chunks.map((page) =>
        fetchCandidatesList(
          scope.merchantId,
          scope.workspaceId,
          filters,
          { limit: CHUNK_SIZE, offset: CHUNK_SIZE * page },
          sort,
        ),
      );

      const results = await Promise.all(requests);
      return results.flatMap((r) => r.candidates);
    },
  });
};

const CANDIDATES_METRICS_BATCH_SIZE = 200;

/**
 * Lazily fetches Match Quality scores for the given candidate IDs.
 * Mirrors the UNB-1371 pattern from `useTargetCollaborationsMetrics`.
 *
 * Returns a map of tikTokCreatorOpenId → matchQualityScore (number | null).
 * `null` means the creator has no score for the current NBA model version.
 *
 * The query is enabled only when candidateIds is non-empty. `placeholderData`
 * keeps previously-fetched scores visible during pagination to avoid
 * re-showing skeletons.
 */
export const useCandidatesMatchQuality = (candidateIds: string[]) => {
  const scope = useScopedMerchantClient();

  const cacheKey = [...candidateIds].sort().join(",");

  return useQuery({
    queryKey: ["candidates-match-quality", scope.merchantId, scope.workspaceId, cacheKey],
    queryFn: async (): Promise<Record<string, number | null>> => {
      if (!scope.merchantId || candidateIds.length === 0) return {};

      const batches: string[][] = [];
      for (let i = 0; i < candidateIds.length; i += CANDIDATES_METRICS_BATCH_SIZE) {
        batches.push(candidateIds.slice(i, i + CANDIDATES_METRICS_BATCH_SIZE));
      }

      const responses = await Promise.all(
        batches.map(async (batch) => {
          const res = scope.workspaceId
            ? await client.workspace[":workspaceId"].merchant[":merchantId"].candidates.metrics.$post({
                param: scope.param,
                json: { candidateIds: batch },
              })
            : await client.merchant[":merchantId"].candidates.metrics.$post({
                param: scope.param,
                json: { candidateIds: batch },
              });
          if (!res.ok) throw await handleError(res);
          return await res.json();
        }),
      );

      const map: Record<string, number | null> = {};
      for (const data of responses) {
        for (const m of data.metrics) {
          map[m.tikTokCreatorOpenId] = m.matchQualityScore;
        }
      }
      return map;
    },
    enabled: !!scope.merchantId && candidateIds.length > 0,
    refetchOnMount: "always",
    staleTime: 0,
    placeholderData: (previous) => previous,
  });
};

export type { Candidate, CandidateFilters, CandidateListItem, CandidateSort };
